/**
 * Markup structure audit. Walks the joined prototypeMarkup DOM after
 * mount and reports any element that lives directly under
 * <main class="app-main"> but is NOT inside a `.view` container.
 * Such elements are markup leaks (extra `</div>`, missing wrapper,
 * or a fragment that forgot to open its view div). They will be
 * masked by the CSS rule
 * `main.app-main > *:not(.view) { display: none !important }`
 * but the warning is what tells us the markup needs a structural
 * fix — the CSS mask is only a safety net, not a fix.
 *
 * Runs in the browser console only on dev mounts. Returns the list
 * of orphans so smoke tests can assert no-leak.
 */
import type { Root } from "./prototypeTypes";

export interface ViewLeak {
  tag: string;
  id: string;
  classes: string;
  preview: string;
}

export function auditPrototypeViewStructure(root: Root): ViewLeak[] {
  if (typeof document === "undefined") return [];
  const main = root.querySelector?.<HTMLElement>("main.app-main")
    ?? (typeof document !== "undefined" ? document.querySelector("main.app-main") : null);
  if (!main) return [];
  const orphans: ViewLeak[] = [];
  for (const child of Array.from(main.children)) {
    if (child.tagName === "STYLE" || child.tagName === "SCRIPT") continue;
    if (child.classList.contains("view")) continue;
    const text = (child.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 60);
    orphans.push({
      tag: child.tagName,
      id: child.id,
      classes: child.className.toString().slice(0, 80),
      preview: text,
    });
  }
  if (orphans.length > 0) {
    // eslint-disable-next-line no-console -- dev warning
    console.warn(
      `[deepmox] markup leak: ${orphans.length} element(s) directly under <main> outside any .view container. ` +
      `These will be hidden by CSS but the markup needs a structural fix.`,
      orphans,
    );
  }
  return orphans;
}
